import React, { useEffect, useState, useRef } from 'react'
import { Button, Stepper, Step, StepLabel, SelectChangeEvent, Typography, Divider, Paper } from '@mui/material'
import { Box } from '@mui/system'
import { People, CheckCircle } from '@mui/icons-material'
import TaskDetails from './Steps/TaskDetails'
import TaskAssignment, { TaskAssignmentRef } from './Steps/TaskAssignment'
import { useLoading } from '../../../../context/LoadingContext'
import useConfirmationAlert from '../../../../hooks/componentHooks/useConfirmationAlert'
import LoadingBackdrop from '../../../Common/Loading/LoadingBackdrop'
import useAddTask from '../../../../hooks/taskHooks/useAddTask'
import useUpdateTask from '../../../../hooks/taskHooks/useUpdateTask'
import { validateDate, validateString, validateURL } from '../../../../utils/validationUtils'
import { Task, User } from '../../../../redux/task/types'
import { useAppSelector } from '../../../../redux/store'
import { selectUserTeamMemberInfo } from '../../../../redux/auth/selector'
import { useFetchTeamMembers } from '../../../../hooks/teamMemberHooks/useFetchTeamMembers'
import { gradientColors, textColors } from '../../../Common/Color/Color'
import { fontSizes } from '../../../Common/Typography/TypographyStyles'

interface TaskFormProps {
  taskDetails?: Task
  onSave: () => void
  onChange: (hasChanges: boolean) => void
}

const STEPS = ['Task Details', 'Assignment'] as const

type FormData = {
  title: string
  details: string
  importance: string
  operationalManager: string | string[]
  functionalArea: string | string[]
  supervisor: string | string[]
  dueDate: Date | null
  link: string
  required: boolean
}

type ErrorType = {
  [K in keyof FormData]?: string
}

const TaskForm: React.FC<TaskFormProps> = ({ taskDetails, onSave, onChange }) => {
  const { loading } = useLoading()
  const { addTask } = useAddTask()
  const { handleUpdateTask } = useUpdateTask()
  const { ConfirmationAlert, openConfirmationAlert } = useConfirmationAlert()
  const [activeStep, setActiveStep] = useState(0)
  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)
  // Re-enable team member fetching now that authentication is working
  const { teamMembers } = useFetchTeamMembers()
  
  // Ref for TaskAssignment component to access validation function
  const taskAssignmentRef = useRef<TaskAssignmentRef>(null)

  const [formData, setFormData] = useState<FormData>({
    title: taskDetails?.title || '',
    details: taskDetails?.details || '',
    importance: taskDetails?.importance || '',
    operationalManager: taskDetails?.originalOperationalManagerSelection && taskDetails.originalOperationalManagerSelection.length > 0
      ? taskDetails.originalOperationalManagerSelection
      : taskDetails?.operationalManager && taskDetails.operationalManager.length > 0 
        ? taskDetails.operationalManager.map(om => om.workdayId)
        : [],
    functionalArea: taskDetails?.originalFunctionalAreaSelection && taskDetails.originalFunctionalAreaSelection.length > 0
      ? taskDetails.originalFunctionalAreaSelection
      : taskDetails?.functionalArea && taskDetails.functionalArea.length > 0 
        ? taskDetails.functionalArea
        : [],
    supervisor: taskDetails?.originalSupervisorSelection && taskDetails.originalSupervisorSelection.length > 0
      ? taskDetails.originalSupervisorSelection
      : taskDetails?.supervisor && taskDetails.supervisor.length > 0 
        ? taskDetails.supervisor.map(s => s.workdayId)
        : [],
    dueDate: taskDetails?.dueDate ? new Date(taskDetails.dueDate) : null,
    link: taskDetails?.link || '',
    required: taskDetails?.required || false,
  })

  const [errors, setErrors] = useState<ErrorType>({})
  const [initialFormData] = useState<FormData>(formData)

  const isUpdateTask = Boolean(taskDetails?.id)

  useEffect(() => {
    const formChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData)
    onChange(formChanged)
  }, [formData, initialFormData, onChange])

  const validateStep = (stepIndex: number): ErrorType => {
    const stepErrors: ErrorType = {}

    if (stepIndex === 0) {
      // Validate Task Details step
      const titleError = validateString(formData.title, 'Task title', { required: true })
      if (titleError) stepErrors.title = titleError

      const detailsError = validateString(formData.details, 'Task details', { required: true })
      if (detailsError) stepErrors.details = detailsError

      const importanceError = validateString(formData.importance, 'Task importance', { required: true })
      if (importanceError) stepErrors.importance = importanceError

      const linkError = validateURL(formData.link, 'Task link', { required: false })
      if (linkError) stepErrors.link = linkError

      const dueDateError = validateDate(formData.dueDate?.toISOString(), 'Due date', { required: true, future: true })
      if (dueDateError) stepErrors.dueDate = dueDateError
    }

    if (stepIndex === 1) {
      // Validate Assignment step - all three fields are required
      const hasOperationalManager = formData.operationalManager && formData.operationalManager.toString().trim() !== ''
      const hasFunctionalArea = formData.functionalArea && formData.functionalArea.toString().trim() !== ''
      const hasSupervisor = formData.supervisor && formData.supervisor.toString().trim() !== ''
      
      if (!hasOperationalManager) {
        stepErrors.operationalManager = 'Operations/Practice Manager is required'
      }
      if (!hasFunctionalArea) {
        stepErrors.functionalArea = 'Functional Area is required'
      }
      if (!hasSupervisor) {
        stepErrors.supervisor = 'Supervisor is required'
      }
    }

    return stepErrors
  }

  const handleNext = () => {
    const stepErrors = validateStep(activeStep)
    if (Object.values(stepErrors).some((error) => error)) {
      setErrors((prevErrors) => ({ ...prevErrors, ...stepErrors }))
      return
    }
    setActiveStep((prevStep) => prevStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1)
  }


  const handleInputChange = (
    eventOrValue:
      | string
      | string[]
      | boolean
      | Date
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string | string[]>
      | null,
    fieldName?: string,
  ) => {
    if (fieldName) {
      setFormData((prev) => ({ ...prev, [fieldName]: eventOrValue }))
      setErrors((prev) => ({ ...prev, [fieldName]: '' }))
    } else if (eventOrValue && typeof eventOrValue === 'object' && 'target' in eventOrValue) {
      const { name, value } = eventOrValue.target
      setFormData((prev) => ({ ...prev, [name]: value }))
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  // Handle onBlur validation for due date field
  const handleDueDateBlur = () => {
    if (formData.dueDate) {
      const dateString = formData.dueDate.toISOString()
      const validationError = validateDate(dateString, 'Due date', { required: true, future: true })
      
      if (validationError) {
        setErrors((prev) => ({ ...prev, dueDate: validationError }))
      } else {
        setErrors((prev) => ({ ...prev, dueDate: '' }))
      }
    }
  }

  // Handle date picker accept (OK button click) - this is where we show the confirmation
  const handleDueDateAccept = (newDate: Date | null) => {
    if (newDate && isUpdateTask && taskDetails?.dueDate) {
      const originalDate = new Date(taskDetails.dueDate)
      
      // If original date is in the past and new date is in the future
      if (isDateInPast(originalDate) && !isDateInPast(newDate)) {
        const confirmationContent = (
          <Box sx={{ minWidth: 400 }}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                mb: 3, 
                background: gradientColors.redGradient, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: '#d32f2f'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ color: textColors.white, fontWeight: 600, fontSize: fontSizes.size20 }}>
                    Due Date Change Warning
                  </Typography>
                  <Typography sx={{ color: textColors.white, mt: 1 }}>
                    Original Due Date: {originalDate.toLocaleDateString()}
                    <br/>
                    New Due Date: {newDate.toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Typography variant="body1" sx={{ mb: 2 }}>
              You are updating the due date from a past date to a future date. This change:
            </Typography>

            <Box sx={{ ml: 2, mb: 3 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                • May affect task completion metrics
              </Typography>
              <Typography variant="body1">
                • Will reset any overdue status indicators
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Are you sure you want to proceed with this change?
            </Typography>
          </Box>
        )

        openConfirmationAlert({
          title: 'Confirm Due Date Change',
          text: confirmationContent,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          onConfirm: () => {
            // Accept the date change - the date is already updated in formData by onChange
            setErrors((prev) => ({ ...prev, dueDate: '' }))
          },
          onCancel: () => {
            // Revert to original date
            setFormData((prev) => ({ ...prev, dueDate: originalDate }))
          }
        })
      }
    }
  }

  // Helper function to get name from workday ID
  const getNameFromId = (id: string, fieldType: 'operationalManager' | 'supervisor'): string => {
    const member = teamMembers.find(member => {
      if (fieldType === 'operationalManager') {
        return member.operationalManager && member.operationalManager.workdayId === id
      } else if (fieldType === 'supervisor') {
        return member.supervisor && member.supervisor.workdayId === id
      }
      return false
    })
    
    if (fieldType === 'operationalManager') {
      return member?.operationalManager?.name || id
    } else if (fieldType === 'supervisor') {
      return member?.supervisor?.name || id
    }
    return id
  }

  // Helper function to get display value for confirmation (same logic as chips)
  const getDisplayValue = (values: string[], fieldType: 'operationalManager' | 'functionalArea' | 'supervisor') => {
    if (fieldType === 'operationalManager') {
      // If "All" is selected, only show "All Operations/Practice Managers"
      if (values.includes('ALL_OPERATIONAL_MANAGERS')) {
        return ['All Operations/Practice Managers']
      }
      
      // Otherwise, show individual managers
      return values.map(value => {
        return getNameFromId(value, 'operationalManager')
      })
    }
    
    if (fieldType === 'functionalArea') {
      // If "All" is selected, only show "All Functional Areas"
      if (values.includes('ALL_FUNCTIONAL_AREAS')) {
        return ['All Functional Areas']
      }
      
      // Otherwise, show individual functional areas
      return values.filter(value => value !== 'ALL_FUNCTIONAL_AREAS')
    }
    
    if (fieldType === 'supervisor') {
      // If "All" is selected, only show "All Supervisors"
      if (values.includes('ALL_SUPERVISORS')) {
        return ['All Supervisors']
      }
      
      // Otherwise, show individual supervisors
      return values.filter(value => value !== 'ALL_SUPERVISORS').map(value => {
        return getNameFromId(value, 'supervisor')
      })
    }
    
    return values
  }

  // Helper function to calculate total team members that will be assigned
  // This matches the exact backend logic in TaskService.bulkAssignTeamMemberTask
  const calculateTotalTeamMembers = (): number => {
    const operationalManagers = Array.isArray(formData.operationalManager) ? formData.operationalManager : [formData.operationalManager]
    const functionalAreas = Array.isArray(formData.functionalArea) ? formData.functionalArea : [formData.functionalArea]
    const supervisors = Array.isArray(formData.supervisor) ? formData.supervisor : [formData.supervisor]

    // Filter out empty values
    const validOperationalManagers = operationalManagers.filter(om => om && om.toString().trim() !== '')
    const validFunctionalAreas = functionalAreas.filter(fa => fa && fa.toString().trim() !== '')
    const validSupervisors = supervisors.filter(s => s && s.toString().trim() !== '')

    if (validOperationalManagers.length === 0 && validFunctionalAreas.length === 0 && validSupervisors.length === 0) {
      return 0
    }

    // Resolve "All" identifiers to actual individual values (same as backend will receive)
    const resolvedOperationalManager = resolveAllIdentifiers(validOperationalManagers, 'operationalManager')
    const resolvedFunctionalArea = resolveAllIdentifiers(validFunctionalAreas, 'functionalArea')
    const resolvedSupervisor = resolveAllIdentifiers(validSupervisors, 'supervisor')

    // Start with active team members only (matching backend query: { status: "active" })
    let filteredMembers = teamMembers.filter(member => member.status === 'active')

    // Apply filters using AND logic exactly like the backend
    // Backend: if (hasOperationalManager) teamMemberQuery['operationalManager.workdayId'] = { $in: operationalManagerIds }
    if (resolvedOperationalManager.length > 0) {
      filteredMembers = filteredMembers.filter(member => 
        member.operationalManager && resolvedOperationalManager.includes(member.operationalManager.workdayId)
      )
    }

    // Backend: if (hasFunctionalArea) teamMemberQuery.functionalArea = { $in: task.functionalArea }
    if (resolvedFunctionalArea.length > 0) {
      filteredMembers = filteredMembers.filter(member => 
        member.functionalArea && resolvedFunctionalArea.includes(member.functionalArea)
      )
    }

    // Backend: if (hasSupervisor) teamMemberQuery['supervisor.workdayId'] = { $in: supervisorIds }
    if (resolvedSupervisor.length > 0) {
      filteredMembers = filteredMembers.filter(member => 
        member.supervisor && resolvedSupervisor.includes(member.supervisor.workdayId)
      )
    }

    return filteredMembers.length
  }

  // Helper function to generate assignment description JSX
  const generateAssignmentDescriptionJSX = (): React.ReactNode | null => {
    const operationalManagers = Array.isArray(formData.operationalManager) ? formData.operationalManager : [formData.operationalManager]
    const functionalAreas = Array.isArray(formData.functionalArea) ? formData.functionalArea : [formData.functionalArea]
    const supervisors = Array.isArray(formData.supervisor) ? formData.supervisor : [formData.supervisor]

    // Filter out empty values
    const validOperationalManagers = operationalManagers.filter(om => om && om.toString().trim() !== '')
    const validFunctionalAreas = functionalAreas.filter(fa => fa && fa.toString().trim() !== '')
    const validSupervisors = supervisors.filter(s => s && s.toString().trim() !== '')

    if (validOperationalManagers.length === 0 && validFunctionalAreas.length === 0 && validSupervisors.length === 0) {
      return null
    }

    // Get display values using the same logic as chips
    const displayOperationalManagers = getDisplayValue(validOperationalManagers, 'operationalManager')
    const displayFunctionalAreas = getDisplayValue(validFunctionalAreas, 'functionalArea')
    const displaySupervisors = getDisplayValue(validSupervisors, 'supervisor')

    // Generate all possible combinations as JSX elements, but only show valid ones
    const combinations: React.ReactNode[] = []

    // Helper function to create filter chain
    const createFilterChain = (omName?: string, fa?: string, sName?: string): string => {
      const parts: string[] = []
      if (omName) parts.push(omName)
      if (fa) parts.push(fa)
      if (sName) parts.push(sName)
      
      if (parts.length === 1) {
        return parts[0]
      } else if (parts.length === 2) {
        return `${parts[0]} ⮕ ${parts[1]}`
      } else if (parts.length === 3) {
        return `${parts[0]} ⮕ ${parts[1]} ➜ ${parts[2]}`
      }
      return parts.join(' ⮕ ')
    }

    // If we have all three types of assignments, create combinations
    if (displayOperationalManagers.length > 0 && displayFunctionalAreas.length > 0 && displaySupervisors.length > 0) {
      displayOperationalManagers.forEach(omName => {
        displayFunctionalAreas.forEach(fa => {
          displaySupervisors.forEach(sName => {
            const filterChain = createFilterChain(omName, fa, sName)
            combinations.push(
              <Typography key={`${omName}-${fa}-${sName}`} variant="body1" sx={{ mb: 2, fontSize: '16px' }}>
                <strong>{filterChain}</strong>
              </Typography>
            )
          })
        })
      })
    }
    // If we have operational managers and functional areas but no supervisors
    else if (displayOperationalManagers.length > 0 && displayFunctionalAreas.length > 0) {
      displayOperationalManagers.forEach(omName => {
        displayFunctionalAreas.forEach(fa => {
          const filterChain = createFilterChain(omName, fa)
          combinations.push(
            <Typography key={`${omName}-${fa}`} variant="body1" sx={{ mb: 2, fontSize: '16px' }}>
              <strong>{filterChain}</strong>
            </Typography>
          )
        })
      })
    }
    // If we have operational managers and supervisors but no functional areas
    else if (displayOperationalManagers.length > 0 && displaySupervisors.length > 0) {
      displayOperationalManagers.forEach(omName => {
        displaySupervisors.forEach(sName => {
          const filterChain = createFilterChain(omName, undefined, sName)
          combinations.push(
            <Typography key={`${omName}-${sName}`} variant="body1" sx={{ mb: 2, fontSize: '16px' }}>
              <strong>{filterChain}</strong>
            </Typography>
          )
        })
      })
    }
    // If we have functional areas and supervisors but no operational managers
    else if (displayFunctionalAreas.length > 0 && displaySupervisors.length > 0) {
      displayFunctionalAreas.forEach(fa => {
        displaySupervisors.forEach(sName => {
          const filterChain = createFilterChain(undefined, fa, sName)
          combinations.push(
            <Typography key={`${fa}-${sName}`} variant="body1" sx={{ mb: 2, fontSize: '16px' }}>
              <strong>{filterChain}</strong>
            </Typography>
          )
        })
      })
    }
    // If we only have operational managers
    else if (displayOperationalManagers.length > 0) {
      displayOperationalManagers.forEach(omName => {
        const filterChain = createFilterChain(omName)
        combinations.push(
          <Typography key={omName} variant="body1" sx={{ mb: 2, fontSize: '16px' }}>
            <strong>{filterChain}</strong>
          </Typography>
        )
      })
    }
    // If we only have functional areas
    else if (displayFunctionalAreas.length > 0) {
      displayFunctionalAreas.forEach(fa => {
        const filterChain = createFilterChain(undefined, fa)
        combinations.push(
          <Typography key={fa} variant="body1" sx={{ mb: 2, fontSize: '16px' }}>
            <strong>{filterChain}</strong>
          </Typography>
        )
      })
    }
    // If we only have supervisors
    else if (displaySupervisors.length > 0) {
      displaySupervisors.forEach(sName => {
        const filterChain = createFilterChain(undefined, undefined, sName)
        combinations.push(
          <Typography key={sName} variant="body1" sx={{ mb: 2, fontSize: '16px' }}>
            <strong>{filterChain}</strong>
          </Typography>
        )
      })
    }

    if (combinations.length === 0) {
      return null
    }

    return (
      <Box>
        <Typography variant="body1" sx={{ mb: 2, fontSize: '16px' }}>
          This task will be assigned to team members that match ALL these filters:
        </Typography>
        <Box sx={{ ml: 2 }}>
          {combinations}
        </Box>
      </Box>
    )
  }

  // Helper function to resolve "All" identifiers to actual individual values for backend
  const resolveAllIdentifiers = (values: string[], fieldType: 'operationalManager' | 'functionalArea' | 'supervisor'): string[] => {
    // Helper function to ensure values are arrays and filter out empty strings
    const ensureArray = (value: string | string[]): string[] => {
      if (Array.isArray(value)) {
        return value.filter(item => item && item.trim() !== '') // Filter out empty strings and whitespace
      }
      return value && value.trim() !== '' ? [value] : []
    }

    if (fieldType === 'operationalManager') {
      if (values.includes('ALL_OPERATIONAL_MANAGERS')) {
        // Apply the same filtering logic as in TaskAssignment component
        const selectedFunctionalAreas = ensureArray(formData.functionalArea)
        const selectedSupervisors = ensureArray(formData.supervisor)
        
        let filteredMembers = teamMembers.filter(member => member.status === 'active' && member.operationalManager && member.operationalManager.workdayId)
        
        // Filter by functional areas if selected (exclude ALL_ options from filtering)
        if (selectedFunctionalAreas.length > 0) {
          const actualSelectedAreas = selectedFunctionalAreas.filter(area => area !== 'ALL_FUNCTIONAL_AREAS')
          if (actualSelectedAreas.length > 0) {
            filteredMembers = filteredMembers.filter(member => 
              member.functionalArea && actualSelectedAreas.includes(member.functionalArea)
            )
          }
        }
        
        // Filter by supervisors if selected (exclude ALL_ options from filtering)
        if (selectedSupervisors.length > 0) {
          const actualSelectedSupervisors = selectedSupervisors.filter(id => id !== 'ALL_SUPERVISORS')
          if (actualSelectedSupervisors.length > 0) {
            filteredMembers = filteredMembers.filter(member => 
              member.supervisor && actualSelectedSupervisors.includes(member.supervisor.workdayId)
            )
          }
        }

        // Extract unique operational managers
        const uniqueOperationalManagers = Array.from(new Set(
          filteredMembers.map(member => member.operationalManager.workdayId)
        ))
        return uniqueOperationalManagers
      }
      return values.filter(value => value !== 'ALL_OPERATIONAL_MANAGERS')
    }
    
    if (fieldType === 'functionalArea') {
      if (values.includes('ALL_FUNCTIONAL_AREAS')) {
        // Apply the same filtering logic as in TaskAssignment component
        const selectedOperationalManagers = ensureArray(formData.operationalManager)
        const selectedSupervisors = ensureArray(formData.supervisor)
        
        let filteredMembers = teamMembers.filter(member => member.status === 'active' && member.functionalArea)
        
        // Filter by operational managers if selected (exclude ALL_ options from filtering)
        if (selectedOperationalManagers.length > 0) {
          const actualSelectedManagers = selectedOperationalManagers.filter(id => id !== 'ALL_OPERATIONAL_MANAGERS')
          if (actualSelectedManagers.length > 0) {
            filteredMembers = filteredMembers.filter(member => 
              member.operationalManager && 
              actualSelectedManagers.includes(member.operationalManager.workdayId)
            )
          }
        }
        
        // Filter by supervisors if selected (exclude ALL_ options from filtering)
        if (selectedSupervisors.length > 0) {
          const actualSelectedSupervisors = selectedSupervisors.filter(id => id !== 'ALL_SUPERVISORS')
          if (actualSelectedSupervisors.length > 0) {
            filteredMembers = filteredMembers.filter(member => 
              member.supervisor && actualSelectedSupervisors.includes(member.supervisor.workdayId)
            )
          }
        }

        // Extract functional areas
        const functionalAreas = filteredMembers
          .map(member => member.functionalArea)
          .filter(area => area) // Remove any null/undefined values
        
        // Remove duplicates
        return Array.from(new Set(functionalAreas))
      }
      return values.filter(value => value !== 'ALL_FUNCTIONAL_AREAS')
    }
    
    if (fieldType === 'supervisor') {
      if (values.includes('ALL_SUPERVISORS')) {
        // Apply the same filtering logic as in TaskAssignment component
        const selectedOperationalManagers = ensureArray(formData.operationalManager)
        const selectedFunctionalAreas = ensureArray(formData.functionalArea)
        
        let filteredMembers = teamMembers.filter(member => member.status === 'active' && member.supervisor && member.supervisor.workdayId)
        
        // Filter by operational managers if selected (exclude ALL_ options from filtering)
        if (selectedOperationalManagers.length > 0) {
          const actualSelectedManagers = selectedOperationalManagers.filter(id => id !== 'ALL_OPERATIONAL_MANAGERS')
          if (actualSelectedManagers.length > 0) {
            filteredMembers = filteredMembers.filter(member => 
              member.operationalManager && 
              actualSelectedManagers.includes(member.operationalManager.workdayId)
            )
          }
        }
        
        // Filter by functional areas if selected (exclude ALL_ options from filtering)
        if (selectedFunctionalAreas.length > 0) {
          const actualSelectedAreas = selectedFunctionalAreas.filter(area => area !== 'ALL_FUNCTIONAL_AREAS')
          if (actualSelectedAreas.length > 0) {
            filteredMembers = filteredMembers.filter(member => 
              member.functionalArea && actualSelectedAreas.includes(member.functionalArea)
            )
          }
        }

        // Extract unique supervisors
        const uniqueSupervisors = Array.from(new Set(
          filteredMembers.map(member => member.supervisor.workdayId)
        ))
        return uniqueSupervisors
      }
      return values.filter(value => value !== 'ALL_SUPERVISORS')
    }
    
    return values
  }

  // Helper function to check if a date is in the past
  const isDateInPast = (date: Date | null): boolean => {
    if (!date) return false
    const currentDate = new Date()
    // Reset time to start of day for both dates to compare just the dates
    const today = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())
    const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    return compareDate < today
  }

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Validate all steps before submitting
    const allStepErrors = {
      ...validateStep(0),
      ...validateStep(1),
    }

    if (Object.values(allStepErrors).some((error) => error)) {
      setErrors((prevErrors) => ({ ...prevErrors, ...allStepErrors }))
      return
    }

    // Check for incompatible assignments before proceeding
    if (taskAssignmentRef.current && !taskAssignmentRef.current.validateAssignments()) {
      // Validation failed, incompatibility dialog will be shown
      return
    }


    // Proceed with form submission
    submitFormData()
  }

  // Function to handle the actual form submission
  const submitFormData = () => {
    // Generate assignment description JSX
    const assignmentDescriptionJSX = generateAssignmentDescriptionJSX()
    
    // Calculate total team members that will be assigned
    const totalTeamMembers = calculateTotalTeamMembers()
    
    // Create enhanced confirmation content with visual elements
    const confirmationContent = (
      <Box sx={{ minWidth: 400 }}>
        {/* Team Member Count Highlight - Moved to top */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 2, 
            mb: 3, 
            background: gradientColors.greenGradient, 
            borderRadius: 2,
            border: '1px solid',
            borderColor: '#388e3c'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <People sx={{ fontSize: 24, color: textColors.white }} />
            <Box>
              <Typography variant="h6" sx={{ color: textColors.white, fontWeight: 600, fontSize: fontSizes.size20 }}>
                Total Recipients
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: textColors.white, fontSize: '24px' }}>
                {totalTeamMembers} team members
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Assignment Details */}
        {assignmentDescriptionJSX && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Assignment Criteria
              </Typography>
            </Box>
            <Paper elevation={1} sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              {assignmentDescriptionJSX}
            </Paper>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Confirmation Question */}
        <Typography 
          variant="body1" 
          sx={{ 
            fontSize: '16px', 
            textAlign: 'center',
            color: 'text.primary',
            fontWeight: 500
          }}
        >
          {isUpdateTask 
            ? 'Are you sure you want to update this task assignment?' 
            : 'Are you sure you want to create this task assignment?'
          }
        </Typography>
      </Box>
    )

    openConfirmationAlert({
      title: isUpdateTask ? 'Update Task' : 'Create New Task',
      text: confirmationContent,
      onConfirm: async () => {
        // Prepare original selections and resolved values
        const originalOperationalManagerSelection = Array.isArray(formData.operationalManager) ? formData.operationalManager : [formData.operationalManager]
        const originalFunctionalAreaSelection = Array.isArray(formData.functionalArea) ? formData.functionalArea : [formData.functionalArea]
        const originalSupervisorSelection = Array.isArray(formData.supervisor) ? formData.supervisor : [formData.supervisor]

        // Resolve "All" identifiers to actual individual values for backend processing
        const resolvedOperationalManager = resolveAllIdentifiers(originalOperationalManagerSelection, 'operationalManager')
        const resolvedFunctionalArea = resolveAllIdentifiers(originalFunctionalAreaSelection, 'functionalArea')
        const resolvedSupervisor = resolveAllIdentifiers(originalSupervisorSelection, 'supervisor')

        if (isUpdateTask && taskDetails?.id) {
          await handleUpdateTask({ 
            id: taskDetails.id,
            title: formData.title,
            details: formData.details,
            dueDate: formData.dueDate,
            link: formData.link,
            importance: formData.importance,
            operationalManager: resolvedOperationalManager,
            functionalArea: resolvedFunctionalArea,
            supervisor: resolvedSupervisor,
            // Send original selections to preserve user intent
            originalOperationalManagerSelection: originalOperationalManagerSelection.filter(item => item && item.trim() !== ''),
            originalFunctionalAreaSelection: originalFunctionalAreaSelection.filter(item => item && item.trim() !== ''),
            originalSupervisorSelection: originalSupervisorSelection.filter(item => item && item.trim() !== ''),
            status: taskDetails.status || 'Not Started',
            createdBy: taskDetails.createdBy?.email || teamMemberDetails.email,
            required: formData.required,
            teamMembers: teamMembers,
          })
        } else {
          const createdBy: User = {
            email: teamMemberDetails.email,
            workdayId: teamMemberDetails.workdayId,
          }
          await addTask({
            title: formData.title,
            details: formData.details,
            dueDate: formData.dueDate,
            link: formData.link,
            importance: formData.importance,
            operationalManager: resolvedOperationalManager,
            functionalArea: resolvedFunctionalArea,
            supervisor: resolvedSupervisor,
            // Send original selections to preserve user intent
            originalOperationalManagerSelection: originalOperationalManagerSelection.filter(item => item && item.trim() !== ''),
            originalFunctionalAreaSelection: originalFunctionalAreaSelection.filter(item => item && item.trim() !== ''),
            originalSupervisorSelection: originalSupervisorSelection.filter(item => item && item.trim() !== ''),
            status: 'Not Started',
            createdBy,
            required: formData.required,
            teamMembers: teamMembers,
          })
        }
        onSave()
      },
    })
  }

  const handleClear = () => {
    if (activeStep === 0) {
      // Clear only Task Details tab fields
      setFormData((prev) => ({
        ...prev,
        title: '',
        details: '',
        importance: '',
        dueDate: null,
        link: '',
      }))
      setErrors((prev) => ({
        ...prev,
        title: '',
        details: '',
        importance: '',
        dueDate: '',
        link: '',
      }))
    } else if (activeStep === 1) {
      // Clear only Assignment tab fields including documentation required checkbox
      setFormData((prev) => ({
        ...prev,
        operationalManager: [],
        functionalArea: [],
        supervisor: [],
        required: false,
      }))
      setErrors((prev) => ({
        ...prev,
        operationalManager: '',
        functionalArea: '',
        supervisor: '',
      }))
    }
  }

  const isValueEmpty = (value: any) => {
    if (typeof value === 'string') return value.trim() === ''
    if (Array.isArray(value)) return value.length === 0
    if (value && typeof value === 'object') return Object.keys(value).length === 0
    return value === null || value === undefined
  }

  const isCurrentTabEmpty = React.useMemo(() => {
    if (activeStep === 0) {
      // Check if Task Details tab is empty
      return [
        formData.title,
        formData.details,
        formData.importance,
        formData.link,
        formData.dueDate,
      ].every(isValueEmpty)
    } else if (activeStep === 1) {
      // Check if Assignment tab is empty (including documentation required checkbox)
      return [
        formData.operationalManager,
        formData.functionalArea,
        formData.supervisor,
      ].every(isValueEmpty) && !formData.required
    }
    return true
  }, [formData, activeStep])

  // Check if Create/Update button should be disabled
  const isSubmitButtonDisabled = React.useMemo(() => {
    // All three assignment fields are required
    const hasOperationalManager = formData.operationalManager && formData.operationalManager.toString().trim() !== ''
    const hasFunctionalArea = formData.functionalArea && formData.functionalArea.toString().trim() !== ''
    const hasSupervisor = formData.supervisor && formData.supervisor.toString().trim() !== ''
    
    return !hasOperationalManager || !hasFunctionalArea || !hasSupervisor
  }, [formData.operationalManager, formData.functionalArea, formData.supervisor])

  const detailsError = {
    title: errors.title || '',
    details: errors.details || '',
    importance: errors.importance || '',
    link: errors.link || '',
    dueDate: errors.dueDate || '',
  }

  const assignmentError = {
    operationalManager: errors.operationalManager || '',
    supervisor: errors.supervisor || '',
    functionalArea: errors.functionalArea || '',
  }

  const StepContent = [
    <TaskDetails
      key='details'
      formData={{
        title: formData.title,
        details: formData.details,
        importance: formData.importance,
        link: formData.link,
        dueDate: formData.dueDate,
      }}
      handleChange={handleInputChange}
      handleDueDateBlur={handleDueDateBlur}
      handleDueDateAccept={handleDueDateAccept}
      error={detailsError}
    />,
    <TaskAssignment
      key='assignment'
      ref={taskAssignmentRef}
      formData={{
        operationalManager: formData.operationalManager,
        supervisor: formData.supervisor,
        functionalArea: formData.functionalArea,
        required: formData.required,
      }}
      handleChange={handleInputChange}
      error={assignmentError}
      teamMembers={teamMembers}
    />,
  ]

  return (
    <form onSubmit={handleFormSubmit} autoComplete='off'>
      <Box sx={{ width: '100%' }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label, index) => (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ my: 2 }}>{StepContent[activeStep]}</Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>{activeStep !== 0 && <Button onClick={handleBack}>Back</Button>}</Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {!isUpdateTask && (
              <Button color='info' onClick={handleClear} disabled={isCurrentTabEmpty}>
                Clear
              </Button>
            )}
            {activeStep === STEPS.length - 1 && (
              <Button 
                type='submit' 
                variant='contained' 
                color={isUpdateTask ? 'primary' : 'success'}
                disabled={isSubmitButtonDisabled}
              >
                {isUpdateTask ? 'Update' : 'Create'}
              </Button>
            )}
            {activeStep !== STEPS.length - 1 && (
              <Button variant='contained' color='primary' onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      {loading && (
        <LoadingBackdrop message={isUpdateTask ? 'Updating task...' : 'Creating task...'} />
      )}
      {ConfirmationAlert}
    </form>
  )
}

export default TaskForm
